/**
 * Themed button — RN counterpart of the web widget's `ui/button.tsx`.
 *
 * The web version is shadcn/cva over Tailwind classes. That machinery does not
 * survive the crossing (no class strings, no cascade, and `variant` has to
 * resolve to concrete colours rather than semantic tokens), so the variants are
 * re-expressed as style objects resolved against the live widget theme.
 *
 * The variant and size NAMES are kept identical to web on purpose: the call
 * sites are ports of the same tree, and a reviewer comparing the two files
 * should see `variant="outline" size="icon"` in both.
 *
 * A gradient `primaryColor` cannot be a `backgroundColor`, so the default
 * variant paints through <GradientFill> and every other variant — which are
 * flat by definition — takes the cheap path.
 */

import type { FC, ReactNode } from "react";
import { StyleSheet, Text, View, type StyleProp, type TextStyle, type ViewStyle } from "react-native";
import type { ResolvedWidgetTheme } from "@ringg/core";

import { GradientFill, solidColor } from "../../lib/gradient";
import { resolveFontFamily, useWidgetTheme } from "../../lib/widget-theme";
import { Touchable } from "./pressable";

export type ButtonVariant = "default" | "outline" | "ghost" | "destructive";
export type ButtonSize = "default" | "sm" | "lg" | "icon";

export interface ButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  onPress?: () => void;
  disabled?: boolean;
  /** Overrides the resolved variant surface — used for per-config styling. */
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  testID?: string;
  accessibilityLabel?: string;
  /** A string renders as themed label text; nodes render as-is. */
  children?: ReactNode;
}

interface VariantSurface {
  /** May be a gradient string — resolved by the renderer, not here. */
  background: string;
  foreground: string;
  borderColor?: string;
}

const SIZES: Record<ButtonSize, ViewStyle> = {
  default: { height: 36, paddingHorizontal: 16 },
  sm: { height: 32, paddingHorizontal: 12 },
  lg: { height: 40, paddingHorizontal: 32 },
  icon: { height: 36, width: 36 },
};

const TEXT_SIZES: Record<ButtonSize, number> = { default: 14, sm: 12, lg: 14, icon: 14 };

const surfaceFor = (variant: ButtonVariant, theme: ResolvedWidgetTheme): VariantSurface => {
  switch (variant) {
    case "outline":
      return { background: theme.surfaceColor, foreground: theme.textColor, borderColor: solidColor(theme.borderColor) };
    case "ghost":
      // Web's ghost is transparent until hover; RN's press feedback is the
      // opacity dip on Touchable, so the resting state is simply transparent.
      return { background: "transparent", foreground: theme.mutedTextColor };
    case "destructive":
      return { background: theme.errorColor, foreground: "#ffffff" };
    case "default":
    default:
      return { background: theme.primaryColor, foreground: theme.primaryTextColor };
  }
};

export const Button: FC<ButtonProps> = ({ variant = "default", size = "default", onPress, disabled = false, style, textStyle, testID, accessibilityLabel, children }) => {
  const theme = useWidgetTheme();
  const surface = surfaceFor(variant, theme);

  const content =
    typeof children === "string" ? (
      <Text style={[styles.label, { color: surface.foreground, fontSize: TEXT_SIZES[size], fontFamily: resolveFontFamily(theme.fontFamily) }, textStyle]} numberOfLines={1}>
        {children}
      </Text>
    ) : (
      children
    );

  // Every variant but `default` is flat, so it skips the SVG layer entirely.
  const body =
    variant === "default" ? (
      <GradientFill color={surface.background} style={[styles.base, SIZES[size], style]}>
        {content}
      </GradientFill>
    ) : (
      <View style={[styles.base, SIZES[size], { backgroundColor: surface.background }, surface.borderColor ? { borderWidth: StyleSheet.hairlineWidth, borderColor: surface.borderColor } : null, style]}>{content}</View>
    );

  return (
    <Touchable onPress={onPress} disabled={disabled} testID={testID} accessibilityLabel={accessibilityLabel}>
      {body}
    </Touchable>
  );
};

const styles = StyleSheet.create({
  base: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderRadius: 6,
  },
  label: { fontWeight: "500" },
});
